/**
 * The sound of the table, synthesised rather than sampled.
 *
 * Every cue here is built from oscillators and noise generated at runtime, so
 * the game ships no audio files at all: nothing to download, nothing to
 * licence, nothing that can be a copy of somebody else's recording. The whole
 * module costs a couple of kilobytes and the browser does the rest.
 *
 * Three rules the cues are written to:
 *
 *   1. Nothing here carries information. Every sound accompanies something the
 *      table already says in pictures and in words, so a muted game - or a
 *      player who never enables sound at all - loses nothing but pleasure.
 *   2. Nothing is loud and nothing is long. The longest cue is under a second,
 *      and the one a player hears most, the discard, is forty milliseconds.
 *   3. Nothing starts on its own. The AudioContext is created on the first cue
 *      and, if the browser has not yet seen a gesture it trusts, the attempt
 *      fails quietly and the game carries on in silence.
 *
 * The preference is remembered, and it defaults to off. A game that starts
 * making noise in a room where that was not expected has done something rude,
 * and the toggle is one click away in the rack.
 */

export type SoundCue =
  | "click"
  | "lift"
  | "discard"
  | "draw"
  | "shuffle"
  | "dice"
  | "claim"
  | "win";

const STORAGE_KEY = "kikik.mahjong.sound";

let context: AudioContext | null = null;
let master: GainNode | null = null;
let enabled: boolean | null = null;

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const scope = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return scope.AudioContext ?? scope.webkitAudioContext ?? null;
}

/** Whether sound is on. Off until a player asks for it. */
export function isSoundOn(): boolean {
  if (enabled !== null) return enabled;
  try {
    enabled = globalThis.localStorage?.getItem(STORAGE_KEY) === "on";
  } catch {
    enabled = false;
  }
  return enabled;
}

export function setSoundOn(on: boolean): void {
  enabled = on;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    // A browser that refuses storage still gets sound for this session.
  }
  // Turning it on is itself a gesture, so this is the safe moment to open the
  // context: the first cue then plays immediately instead of being swallowed.
  if (on) void ensureContext();
}

/**
 * The audio graph, opened on demand.
 *
 * Browsers only allow an AudioContext to run after a gesture they trust. If
 * this is called too early the context exists but stays suspended, every cue
 * scheduled on it is inaudible, and nothing throws - which is exactly the
 * behaviour we want. The resume attempt on each call is what picks it up again
 * the moment a real click arrives.
 */
async function ensureContext(): Promise<AudioContext | null> {
  const Ctor = audioContextCtor();
  if (!Ctor) return null;
  if (!context) {
    try {
      context = new Ctor();
      master = context.createGain();
      // Everything is mixed well below unity: these are table noises sitting
      // under whatever else the player is listening to, not a soundtrack.
      master.gain.value = 0.22;
      master.connect(context.destination);
    } catch {
      context = null;
      return null;
    }
  }
  if (context.state === "suspended") {
    try {
      await context.resume();
    } catch {
      // Still no gesture. Silence is the correct outcome.
    }
  }
  return context;
}

/** A short burst of noise, band-limited - the body of every knock on this table. */
function knock(
  ctx: AudioContext,
  out: AudioNode,
  at: number,
  { hz, q, gain, decay }: { hz: number; q: number; gain: number; decay: number },
): void {
  const length = Math.max(1, Math.ceil(ctx.sampleRate * decay));
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let i = 0; i < length; i += 1) {
    // White noise under an exponential envelope: a struck object, not a hiss.
    samples[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;

  const filter = ctx.createBiquadFilter();
  filter.type = "bandpass";
  filter.frequency.value = hz;
  filter.Q.value = q;

  const level = ctx.createGain();
  level.gain.value = gain;

  source.connect(filter).connect(level).connect(out);
  source.start(at);
  source.stop(at + decay);
}

/** A plain tone with a soft attack, for the two cues that are music rather than wood. */
function tone(
  ctx: AudioContext,
  out: AudioNode,
  at: number,
  { hz, gain, length, type = "sine" }: { hz: number; gain: number; length: number; type?: OscillatorType },
): void {
  const osc = ctx.createOscillator();
  osc.type = type;
  osc.frequency.value = hz;

  const level = ctx.createGain();
  level.gain.setValueAtTime(0.0001, at);
  level.gain.exponentialRampToValueAtTime(gain, at + 0.014);
  level.gain.exponentialRampToValueAtTime(0.0001, at + length);

  osc.connect(level).connect(out);
  osc.start(at);
  osc.stop(at + length + 0.02);
}

/**
 * Each cue, written out.
 *
 * The knock frequencies are all in the 1-3kHz range because that is where a
 * small dense object struck against a hard surface actually lives; a lower
 * knock sounds like a door and a higher one sounds like glass.
 */
function render(ctx: AudioContext, out: AudioNode, cue: SoundCue): void {
  const now = ctx.currentTime;
  switch (cue) {
    case "click":
      knock(ctx, out, now, { hz: 2600, q: 2.4, gain: 0.5, decay: 0.03 });
      break;

    case "lift":
      // Tile leaving the rack: the same knock, quieter and softer-edged.
      knock(ctx, out, now, { hz: 1800, q: 1.6, gain: 0.22, decay: 0.05 });
      break;

    case "discard":
      // The signature sound: ivory hitting felt over a hard table. Two knocks
      // a few milliseconds apart, the second lower - a tile does not land flat.
      knock(ctx, out, now, { hz: 2200, q: 1.8, gain: 0.9, decay: 0.045 });
      knock(ctx, out, now + 0.018, { hz: 1250, q: 1.2, gain: 0.55, decay: 0.07 });
      break;

    case "draw":
      knock(ctx, out, now, { hz: 3000, q: 3, gain: 0.34, decay: 0.035 });
      break;

    case "shuffle": {
      // Many tiles moving against each other: a long scatter of small knocks,
      // irregular enough that no two shuffles sound the same.
      for (let i = 0; i < 34; i += 1) {
        knock(ctx, out, now + Math.random() * 1.15, {
          hz: 1500 + Math.random() * 1900,
          q: 1.4,
          gain: 0.1 + Math.random() * 0.12,
          decay: 0.03,
        });
      }
      break;
    }

    case "dice": {
      // Two dice tumbling: knocks bunching up, then two final landings.
      let at = now;
      for (let i = 0; i < 7; i += 1) {
        knock(ctx, out, at, { hz: 2400 + i * 130, q: 2.2, gain: 0.3, decay: 0.03 });
        at += 0.075 - i * 0.007;
      }
      knock(ctx, out, at + 0.08, { hz: 1700, q: 1.6, gain: 0.6, decay: 0.06 });
      knock(ctx, out, at + 0.19, { hz: 1500, q: 1.6, gain: 0.5, decay: 0.06 });
      break;
    }

    case "claim":
      // A rising fifth: someone has spoken, and the table is listening.
      tone(ctx, out, now, { hz: 523.25, gain: 0.16, length: 0.11 });
      tone(ctx, out, now + 0.085, { hz: 783.99, gain: 0.15, length: 0.2 });
      break;

    case "win":
      // Four notes up a pentatonic run. Warm, brief, and over before it can
      // start to sound like a slot machine paying out.
      [523.25, 659.25, 783.99, 1046.5].forEach((hz, index) => {
        tone(ctx, out, now + index * 0.1, {
          hz,
          gain: 0.17,
          length: index === 3 ? 0.62 : 0.24,
          type: "triangle",
        });
      });
      break;
  }
}

/**
 * Play a cue, if sound is on and the browser will let us.
 *
 * Never throws and never returns a promise a caller has to handle: sound is a
 * garnish, and no part of the game may depend on it having worked.
 */
export function playCue(cue: SoundCue): void {
  if (!isSoundOn()) return;
  void ensureContext().then((ctx) => {
    if (!ctx || !master || ctx.state !== "running") return;
    try {
      render(ctx, master, cue);
    } catch {
      // A cue that cannot be built is simply not heard.
    }
  });
}

/** Test seam: forget the cached preference and the open context. */
export function resetAudioForTests(): void {
  enabled = null;
  context = null;
  master = null;
}
